import { useMobileScrollReveal } from "@/hooks/useMobileScrollReveal";
import { useId, type ReactNode } from 'react';
import { TruWeIcon } from './TruWeIcon';
import type { ConversionCopy } from './content';
import './conversion.css';

export function BenefitList({ copy }: { copy: ConversionCopy }) {
  return <ul className="twc-benefits">{copy.benefits.map(b => <li key={b.label}><TruWeIcon name={b.icon} /><span>{b.label}</span></li>)}</ul>;
}

/** One DOM for mobile and desktop. Consumer passes the existing live buy box and gallery. */
export function DecisionHero({ copy, gallery, purchase, proof }: { copy: ConversionCopy; gallery: ReactNode; purchase: ReactNode; proof?: ReactNode }) {
  return <section className="twc-decision" data-engagement-section="hero" aria-label={`${copy.title} overview`}>
    <header className="twc-intro"><p className="twc-eyebrow">{copy.title}</p><h1>{copy.headline}</h1><p className="twc-lede">{copy.description}</p></header>
    <div className="twc-gallery">{gallery}</div>
    <div className="twc-summary"><BenefitList copy={copy} />{proof}<a className="twc-text-link" href="#twc-ingredients">Ingredients & evidence <TruWeIcon name="arrow" size={18} /></a></div>
    <div className="twc-purchase" id="buy-box" data-engagement-section="buy-box">{purchase}</div>
  </section>;
}

export function PageSection({ id, eyebrow, title, children, engagement }: { id: string; eyebrow: string; title: string; children: ReactNode; engagement?: string }) {
  const headingId = useId();
  const headingReveal = useMobileScrollReveal<HTMLHeadingElement>();
  return <section id={id} className="twc-section" aria-labelledby={headingId} data-engagement-section={engagement}>
    <p className="twc-eyebrow">{eyebrow}</p><h2 ref={headingReveal} id={headingId}>{title}</h2>{children}
  </section>;
}

export function NeedsSection({ copy }: { copy: ConversionCopy }) {
  return <PageSection id="twc-for-you" eyebrow="Is this for me?" title="Does this sound like your routine?" engagement="tldr">
    <ul className="twc-needs">{copy.needs.map(n => <li key={n.title}><TruWeIcon name={n.icon} size={28} /><div><h3>{n.title}</h3><p>{n.body}</p></div></li>)}</ul>
  </PageSection>;
}

export type FormulaItem = { name: string; detail?: string; dose?: string; evidenceUrl?: string };
export function FormulaSection({ copy, items, children, bare = false }: { copy: ConversionCopy; items?: FormulaItem[]; children?: ReactNode; bare?: boolean }) {
  // `bare` = the slotted content already carries its own heading (e.g. the
  // existing actives showcase), so we must not print a second one.
  if (bare) {
    return <section id="twc-ingredients" className="twc-section" data-engagement-section="ingredients">{children}</section>;
  }
  return <PageSection id="twc-ingredients" eyebrow="Inside the formula" title={copy.formulaHeading} engagement="ingredients">
    {items?.length ? <div className="twc-formula-list">{items.map(item => <details key={item.name}><summary><span>{item.name}{item.dose && <small>{item.dose}</small>}</span><TruWeIcon name="expand" size={20} /></summary><div className="twc-detail">{item.detail && <p>{item.detail}</p>}{item.evidenceUrl && <a className="twc-text-link" href={item.evidenceUrl} target="_blank" rel="noopener noreferrer">Read ingredient research <TruWeIcon name="arrow" size={16} /></a>}</div></details>)}</div> : children}
  </PageSection>;
}

export function EvidenceSection({ children, batchReportUrl }: { children?: ReactNode; batchReportUrl?: string | null }) {
  return <PageSection id="twc-evidence" eyebrow="Make an informed choice" title="See what supports the formula" engagement="reviews">
    <p>Ingredient research and finished-product testing answer different questions. Explore the available evidence below.</p>
    {batchReportUrl && <a className="twc-report" href={batchReportUrl} target="_blank" rel="noopener noreferrer"><TruWeIcon name="report" /><span>View this product’s batch report</span><TruWeIcon name="arrow" size={18} /></a>}
    {children}
  </PageSection>;
}

/** Render approved usage content through this slot; never infer dosage from a mockup. */
export function RoutineSection({ copy, children }: { copy: ConversionCopy; children: ReactNode }) {
  return <PageSection id="twc-routine" eyebrow="Your daily routine" title={copy.routineHeading} engagement="routine">{children}</PageSection>;
}

/** Link to the single selector rather than create a second independent pack state. */
export function OfferRecap({ children, actionLabel = 'Choose my pack' }: { children: ReactNode; actionLabel?: string }) {
  return <PageSection id="twc-offer" eyebrow="Make your choice" title="Choose the routine that fits" engagement="comparison">{children}<a className="twc-primary" href="#buy-box">{actionLabel}<TruWeIcon name="arrow" size={18} /></a></PageSection>;
}

export function BuyerFaq({ items }: { items: { q: string; a: string }[] }) {
  return <PageSection id="twc-faq" eyebrow="Before you choose" title="Your questions, answered" engagement="faq"><div className="twc-formula-list">{items.map(item => <details key={item.q}><summary><span>{item.q}</span><TruWeIcon name="expand" size={20} /></summary><div className="twc-detail"><p>{item.a}</p></div></details>)}</div></PageSection>;
}

export function MobilePurchaseBar({ priceLabel, supplyLabel, action, hidden = false }: { priceLabel?: string; supplyLabel: string; action: ReactNode; hidden?: boolean }) {
  return <aside className="twc-mobile-buy" aria-label="Selected product and purchase action" hidden={hidden}>
    <div>{priceLabel && <strong>{priceLabel}</strong>}<span>{supplyLabel}</span></div><div className="twc-mobile-action">{action}</div>
  </aside>;
}
